import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Config, DriveStep, DriverHook, Popover } from 'driver.js';

import { AbstractGuideService } from '../../../shared-components/guide/core/services/abstract-guide.service';
import { GuideDriverAdapterService } from '../../../shared-components/guide/core/services/guide-driver-adapter.service';
import { StartAutoLotGuideParamsType } from '../types/start-auto-lot-guide-params.type';

@Injectable()
export class AutoLotGuideService extends AbstractGuideService {
  constructor(
    guideDriverAdapterService: GuideDriverAdapterService,
    translateService: TranslateService,
  ) {
    super(guideDriverAdapterService, translateService);
  }

  start(params: StartAutoLotGuideParamsType): void {
    const config: Config = {
      showProgress: true,
      animate: true,
      allowKeyboardControl: true,
      allowClose: true,
      prevBtnText: this.translate('DRIVER_CONFIG.PREV_BTN'),
      nextBtnText: this.translate('DRIVER_CONFIG.NEXT_BTN'),
      doneBtnText: this.translate('DRIVER_CONFIG.DONE_BTN'),
      onDestroyed: this.createCleanupHook(params),
      steps: this.buildAvailableSteps(params),
    };

    this.startGuide(config);
  }

  forceClose(params?: Pick<StartAutoLotGuideParamsType, 'hideGuideLotIcons'>): void {
    this.destroyGuideSafely();
    params?.hideGuideLotIcons();
  }

  private createCleanupHook(params: StartAutoLotGuideParamsType): DriverHook {
    return () => {
      params.hideGuideLotIcons();
    };
  }

  private buildAvailableSteps(params: StartAutoLotGuideParamsType): DriveStep[] {
    return this.buildSteps(params).filter((step) => this.isStepAvailable(step));
  }

  private buildSteps(params: StartAutoLotGuideParamsType): DriveStep[] {
    return [
      this.buildTitleBlockStep(),
      this.buildPhotoGalleryStep(params),
      this.buildAdditionalTagsStep(params),
      this.buildActionButtonsFirstStep(params),
      this.buildActionButtonsSecondStep(),
      this.buildDateOfManufactureFirstStep(),
      this.buildDateOfManufactureSecondStep(),
      this.buildLotDataStep(),
      this.buildAuctionDataStep(),
      this.buildBidsFirstStep(),
      this.buildBidsSecondStep(),
      this.buildSellStatsStep(),
      this.buildAdditionalDescriptionStep(),
      this.buildCalculatorStep(),
      this.buildFinishStep(),
    ];
  }

  private buildTitleBlockStep(): DriveStep {
    return this.buildStep(
      'title-block',
      'AUTO_LOT.GUIDE.TITLE_BLOCK.TITLE',
      'AUTO_LOT.GUIDE.TITLE_BLOCK.DESCR',
    );
  }

  private buildPhotoGalleryStep(params: StartAutoLotGuideParamsType): DriveStep {
    return this.buildStep(
      'photo-gallery',
      'AUTO_LOT.GUIDE.PHOTO_GALLERY.TITLE',
      'AUTO_LOT.GUIDE.PHOTO_GALLERY.DESCR',
      {
        onNextClick: () => {
          params.showGuideLotIcons();
          this.refreshGuide();
          this.moveNext();
        },
      },
    );
  }

  private buildAdditionalTagsStep(params: StartAutoLotGuideParamsType): DriveStep {
    return this.buildStep(
      'additional-tags',
      'AUTO_LOT.GUIDE.ADDITIONAL_TAGS.TITLE',
      'AUTO_LOT.GUIDE.ADDITIONAL_TAGS.DESCR',
      {
        onPrevClick: () => {
          params.hideGuideLotIcons();
          this.refreshGuide();
          this.movePrevious();
        },
        onNextClick: () => {
          params.hideGuideLotIcons();
          this.refreshGuide();
          this.moveNext();
        },
      },
    );
  }

  private buildActionButtonsFirstStep(params: StartAutoLotGuideParamsType): DriveStep {
    return this.buildStep(
      'action-buttons-first',
      'AUTO_LOT.GUIDE.ACTION_BTNS.FIRST.TITLE',
      'AUTO_LOT.GUIDE.ACTION_BTNS.FIRST.DESCR',
      {
        onPrevClick: () => {
          params.showGuideLotIcons();
          this.refreshGuide();
          this.movePrevious();
        },
      },
    );
  }

  private buildActionButtonsSecondStep(): DriveStep {
    return this.buildStep(
      'action-buttons-second',
      'AUTO_LOT.GUIDE.ACTION_BTNS.SECOND.TITLE',
      'AUTO_LOT.GUIDE.ACTION_BTNS.SECOND.DESCR',
      {
        side: 'bottom',
      },
    );
  }

  private buildDateOfManufactureFirstStep(): DriveStep {
    return this.buildStep(
      'date-of-manufacture-first',
      'AUTO_LOT.GUIDE.DATE_OF_MANUFACTURE.FIRST.TITLE',
      'AUTO_LOT.GUIDE.DATE_OF_MANUFACTURE.FIRST.DESCR',
    );
  }

  private buildDateOfManufactureSecondStep(): DriveStep {
    return this.buildStep(
      'date-of-manufacture-second',
      'AUTO_LOT.GUIDE.DATE_OF_MANUFACTURE.SECOND.TITLE',
      'AUTO_LOT.GUIDE.DATE_OF_MANUFACTURE.SECOND.DESCR',
      {
        side: 'bottom',
        description: this.buildDateOfManufactureSecondDescription(),
      },
    );
  }

  private buildLotDataStep(): DriveStep {
    return this.buildStep(
      'lot-data',
      'AUTO_LOT.GUIDE.LOT_DATA.TITLE',
      'AUTO_LOT.GUIDE.LOT_DATA.DESCR',
    );
  }

  private buildAuctionDataStep(): DriveStep {
    return this.buildStep(
      'auction-data',
      'AUTO_LOT.GUIDE.AUCTION_DATA.TITLE',
      'AUTO_LOT.GUIDE.AUCTION_DATA.DESCR',
    );
  }

  private buildBidsFirstStep(): DriveStep {
    return this.buildStep(
      'bids-first',
      'AUTO_LOT.GUIDE.BIDS.FIRST.TITLE',
      'AUTO_LOT.GUIDE.BIDS.FIRST.DESCR',
    );
  }

  private buildBidsSecondStep(): DriveStep {
    return this.buildStep(
      'bids-second',
      'AUTO_LOT.GUIDE.BIDS.SECOND.TITLE',
      'AUTO_LOT.GUIDE.BIDS.SECOND.DESCR',
      {
        side: 'bottom',
      },
    );
  }

  private buildSellStatsStep(): DriveStep {
    return this.buildStep(
      'sell-stats',
      'AUTO_LOT.GUIDE.SELL_STATS.TITLE',
      'AUTO_LOT.GUIDE.SELL_STATS.DESCR',
    );
  }

  private buildAdditionalDescriptionStep(): DriveStep {
    return this.buildStep(
      'additional-description',
      'AUTO_LOT.GUIDE.ADDITIONAL_DESCR.TITLE',
      'AUTO_LOT.GUIDE.ADDITIONAL_DESCR.DESCR',
    );
  }

  private buildCalculatorStep(): DriveStep {
    return this.buildStep('calculator', 'AUTO_LOT.GUIDE.CALC.TITLE', 'AUTO_LOT.GUIDE.CALC.DESCR');
  }

  private buildFinishStep(): DriveStep {
    return {
      popover: this.buildPopover({
        titleKey: 'LOT_BIDDING_MODAL.GUIDE.FINISH.TITLE',
        descriptionKey: 'LOT_BIDDING_MODAL.GUIDE.FINISH.DESCR',
      }),
    };
  }

  private buildStep(
    anchor: string,
    titleKey: string,
    descriptionKey: string,
    overrides?: Partial<Popover>,
  ): DriveStep {
    return {
      element: this.buildAnchorSelector(anchor),
      popover: this.buildPopover({
        titleKey,
        descriptionKey,
        overrides,
      }),
    };
  }

  private buildDateOfManufactureSecondDescription(): string {
    return `${this.translate(
      'AUTO_LOT.GUIDE.DATE_OF_MANUFACTURE.SECOND.DESCR',
    )} <a href="https://ecarjp.com/article/faq" target="_blank">${this.translate(
      'AUTO_LOT.GUIDE.DATE_OF_MANUFACTURE.SECOND.LINK',
    )}</a>`;
  }
}
